import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CircularProgress, TextField, Card, CardContent, Typography, Select, MenuItem, Slider, useTheme } from '@mui/material';
import axios from 'axios';
import { RootState, AppDispatch } from '../redux/store';
import { fetchCandidates, Candidate, updateCandidate } from '../profile/redux/profileSlice';
import { BFF_BASE_URL } from '../auth/authApi';
import { Section } from '../shared/components/Section';
import { useSelectedUser } from '../users/hooks/useSelectedUser';
import { GlowAlert } from '../shared/components/GlowAlert';
 

const getCategoryColors = (isDark: boolean): Record<string, { bg: string; text: string }> => {
  if (isDark) {
    return {
      biography: { bg: '#1e40af', text: '#93c5fd' },
      professional: { bg: '#7c2d12', text: '#fdba74' },
      health: { bg: '#7f1d1d', text: '#fca5a5' },
      preference: { bg: '#3b1f6b', text: '#c4b5fd' },
      goal: { bg: '#14532d', text: '#86efac' },
      therapy: { bg: '#663399', text: '#d8b4fe' },
      creative: { bg: '#1e3a5f', text: '#7dd3fc' },
    };
  }
  return {
    biography: { bg: '#dbeafe', text: '#0c4a6e' },
    professional: { bg: '#fed7aa', text: '#92400e' },
    health: { bg: '#fecaca', text: '#7f1d1d' },
    preference: { bg: '#e9d5ff', text: '#581c87' },
    goal: { bg: '#dcfce7', text: '#166534' },
    therapy: { bg: '#f3e8ff', text: '#581c87' },
    creative: { bg: '#cffafe', text: '#164e63' },
  };
};

const getConfidenceColor = (confidence: number): string => {
  if (confidence >= 0.98) return '#22c55e'; // green
  if (confidence >= 0.9) return '#eab308'; // yellow
  return '#ef4444'; // red
};

interface EditingState {
  id: string;
  field: keyof Candidate;
  value: unknown;
}

const ProfilePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const profile = useSelector((state: RootState) => state.profile);
  const users = useSelector((state: RootState) => state.users.users);
  const [editingCell, setEditingCell] = useState<EditingState | null>(null);
  // The fact whose edit is being saved: its row is locked and shows a spinner meanwhile
  const [savingId, setSavingId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'verified' | 'candidate'>('all');
  const [selectedUserId, setSelectedUserId] = useSelectedUser();
  const theme = useTheme();
  const categoryColors = getCategoryColors(theme.palette.mode === 'dark');

  const userCandidates = selectedUserId
    ? profile.candidates.filter(c => c.user_id === selectedUserId)
    : profile.candidates;

  const filteredCandidates = userCandidates.filter(c => {
    if (statusFilter === 'all') return true;
    return c.status === statusFilter;
  });

  const verifiedCount = userCandidates.filter(c => c.status === 'verified').length;
  const candidateCount = userCandidates.filter(c => c.status === 'candidate').length;

  useEffect(() => {
    dispatch(fetchCandidates());
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(fetchCandidates());
  };

  const handleCellEdit = async (id: string, field: keyof Candidate, value: unknown) => {
    if (savingId) return;
    setSavingId(id);
    try {
      const candidate = profile.candidates.find(c => c.id === id);
      if (!candidate) return;

      // Use update endpoint for all fields (including status)
      const updateData: Record<string, unknown> = {
        category: field === 'category' ? value : candidate.category,
        key: field === 'key' ? value : candidate.key,
        value: field === 'value' ? value : candidate.value,
        confidence: field === 'confidence' ? value : candidate.confidence,
        status: field === 'status' ? value : candidate.status,
      };

      await axios.post(`${BFF_BASE_URL}/admin/profile/${id}/update`, updateData);
      dispatch(updateCandidate({ ...candidate, [field]: value }));
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert('Failed to save changes');
    } finally {
      setSavingId(null);
      setEditingCell(null);
    }
  };

  return (
    <Section title="Profile" onRefresh={handleRefresh} refreshing={profile.loading}>
      <div>
        <Typography variant="h6" gutterBottom>
          Extracted Profile Facts
        </Typography>

        <Card sx={{ mb: 2.5, backgroundColor: 'background.paper' }}>
          <CardContent>
            <Typography variant="body2" color="textPrimary" sx={{ lineHeight: 1.6 }}>
              <strong>What is this?</strong> VictorIA automatically extracts structured facts about the user from their conversations,
              learning about their life, health, preferences, goals, and more over time.
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.6, mt: 1 }}>
              <strong>Purpose:</strong> This data enriches VictorIA's understanding of the user to provide more personalized and relevant assistance.
              Unverified entries (candidates) can be reviewed and either approved (verified) or rejected.
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.6, mt: 1 }}>
              <strong>How to use:</strong> Click on any field to edit. Green confidence means high certainty (≥98%),
              yellow means good confidence (≥90%), and red means lower confidence (&lt;90%).
              Use the status dropdown to approve or reject candidates.
            </Typography>
          </CardContent>
        </Card>

        {profile.error && <GlowAlert sx={{ mb: 2.5 }}>{profile.error}</GlowAlert>}
        {profile.loading && <GlowAlert severity="info">Loading profile data...</GlowAlert>}

        {!profile.loading && profile.candidates.length === 0 && (
          <GlowAlert severity="info">No profile data extracted yet.</GlowAlert>
        )}

        {!profile.loading && profile.candidates.length > 0 && (
          <>
            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: theme.palette.text.secondary }}>User:</span>
                <Select
                  size="small"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  style={{ minWidth: '200px' }}
                >
                  <MenuItem value="">All Users ({profile.candidates.length})</MenuItem>
                  {users.map(user => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.display_name || user.email} ({profile.candidates.filter(c => c.user_id === user.id).length})
                    </MenuItem>
                  ))}
                </Select>
              </div>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: theme.palette.text.secondary }}>Status:</span>
                <Select
                  size="small"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  style={{ minWidth: '140px' }}
                >
                  <MenuItem value="all">All ({profile.candidates.length})</MenuItem>
                  <MenuItem value="verified">
                    <span style={{ color: '#86efac' }}>✓ Verified ({verifiedCount})</span>
                  </MenuItem>
                  <MenuItem value="candidate">
                    <span style={{ color: '#fde68a' }}>○ Candidate ({candidateCount})</span>
                  </MenuItem>
                </Select>
              </div>

              <div style={{ display: 'flex', gap: '15px', fontSize: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{
                    display: 'inline-block',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#86efac',
                  }} />
                  <span style={{ color: theme.palette.text.secondary }}>Verified: {verifiedCount}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{
                    display: 'inline-block',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#fde68a',
                  }} />
                  <span style={{ color: theme.palette.text.secondary }}>Candidate: {candidateCount}</span>
                </div>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '13px',
                backgroundColor: theme.palette.background.paper,
              }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <th style={{ textAlign: 'left', padding: '10px', fontWeight: 500, color: theme.palette.text.secondary }}>User</th>
                  <th style={{ textAlign: 'left', padding: '10px', fontWeight: 500, color: theme.palette.text.secondary }}>Category</th>
                  <th style={{ textAlign: 'left', padding: '10px', fontWeight: 500, color: theme.palette.text.secondary }}>Attribute</th>
                  <th style={{ textAlign: 'left', padding: '10px', fontWeight: 500, color: theme.palette.text.secondary }}>Value</th>
                  <th style={{ textAlign: 'left', padding: '10px', fontWeight: 500, color: theme.palette.text.secondary }}>Confidence</th>
                  <th style={{ textAlign: 'left', padding: '10px', fontWeight: 500, color: theme.palette.text.secondary }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '10px', fontWeight: 500, color: theme.palette.text.secondary }}>Updated</th>
                </tr>
              </thead>
              <tbody>
                {filteredCandidates.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: '20px', textAlign: 'center', color: theme.palette.text.secondary }}>
                      No {statusFilter === 'all' ? 'profile' : statusFilter} entries found.
                    </td>
                  </tr>
                ) : filteredCandidates.map((candidate) => (
                  <tr key={candidate.id}
                      style={{
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        cursor: 'pointer',
                        opacity: savingId === candidate.id ? 0.6 : 1,
                        pointerEvents: savingId === candidate.id ? 'none' : 'auto',
                        backgroundColor: candidate.status === 'verified' ? 'rgba(34, 197, 94, 0.05)' : 'rgba(253, 230, 138, 0.05)',
                        borderLeft: `3px solid ${candidate.status === 'verified' ? '#22c55e' : '#fde68a'}`,
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = candidate.status === 'verified' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(253, 230, 138, 0.1)')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = candidate.status === 'verified' ? 'rgba(34, 197, 94, 0.05)' : 'rgba(253, 230, 138, 0.05)')}>
                    <td style={{ padding: '10px', color: theme.palette.text.primary }}>
                      {savingId === candidate.id && <CircularProgress size={12} sx={{ mr: 1 }} aria-label="Saving" />}
                      {candidate.display_name}
                    </td>
                    <td style={{ padding: '10px' }}>
                      {editingCell?.id === candidate.id && editingCell?.field === 'category' ? (
                        <Select
                          size="small"
                          value={editingCell.value}
                          onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                          onBlur={() => handleCellEdit(candidate.id, 'category', editingCell.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === 'Tab') {
                              e.preventDefault();
                              handleCellEdit(candidate.id, 'category', editingCell.value);
                            }
                          }}
                          style={{ minWidth: '110px' }}
                          autoFocus
                        >
                          {Object.keys(categoryColors).map((cat) => (
                            <MenuItem key={cat} value={cat} style={{ textTransform: 'capitalize' }}>
                              {cat}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <span
                          onClick={() => setEditingCell({ id: candidate.id, field: 'category', value: candidate.category })}
                          style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 600,
                            backgroundColor: categoryColors[candidate.category]?.bg || '#333',
                            color: categoryColors[candidate.category]?.text || '#ccc',
                            textTransform: 'capitalize',
                            cursor: 'pointer',
                          }}>
                          {candidate.category}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px', fontFamily: 'monospace', fontSize: '12px', color: theme.palette.secondary.main }}>
                      {editingCell?.id === candidate.id && editingCell?.field === 'key' ? (
                        <TextField
                          size="small"
                          value={editingCell.value}
                          onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                          onBlur={() => handleCellEdit(candidate.id, 'key', editingCell.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === 'Tab') {
                              e.preventDefault();
                              handleCellEdit(candidate.id, 'key', editingCell.value);
                            }
                          }}
                          autoFocus
                        />
                      ) : (
                        <code onClick={() => setEditingCell({ id: candidate.id, field: 'key', value: candidate.key })} style={{ color: theme.palette.secondary.main }}>
                          {candidate.key}
                        </code>
                      )}
                    </td>
                    <td style={{ padding: '10px' }}>
                      {editingCell?.id === candidate.id && editingCell?.field === 'value' ? (
                        <TextField
                          size="small"
                          value={editingCell.value}
                          onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                          onBlur={() => handleCellEdit(candidate.id, 'value', editingCell.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === 'Tab') {
                              e.preventDefault();
                              handleCellEdit(candidate.id, 'value', editingCell.value);
                            }
                          }}
                          fullWidth
                          autoFocus
                        />
                      ) : (
                        <span onClick={() => setEditingCell({ id: candidate.id, field: 'value', value: candidate.value })} style={{ color: theme.palette.text.primary }}>
                          {candidate.value}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px' }}>
                      {editingCell?.id === candidate.id && editingCell?.field === 'confidence' ? (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          minWidth: '200px',
                        }}>
                          <Slider
                            size="small"
                            min={50}
                            max={100}
                            step={1}
                            value={Math.round((editingCell.value as number) * 100)}
                            onChange={(_, val) => setEditingCell({ ...editingCell, value: (val as number) / 100 })}
                            onChangeCommitted={() => handleCellEdit(candidate.id, 'confidence', editingCell.value)}
                            style={{
                              flex: 1,
                              color: getConfidenceColor(editingCell.value as number),
                              transition: 'color 0.2s ease',
                            }}
                            sx={{
                              '& .MuiSlider-thumb': {
                                backgroundColor: getConfidenceColor(editingCell.value as number),
                                transition: 'background-color 0.2s ease',
                              },
                              '& .MuiSlider-track': {
                                backgroundColor: getConfidenceColor(editingCell.value as number),
                                transition: 'background-color 0.2s ease',
                              },
                            }}
                          />
                          <span style={{
                            color: getConfidenceColor(editingCell.value as number),
                            fontWeight: 600,
                            minWidth: '40px',
                            transition: 'color 0.2s ease',
                          }}>
                            {Math.round((editingCell.value as number) * 100)}%
                          </span>
                        </div>
                      ) : (
                        <span
                          onClick={() => setEditingCell({ id: candidate.id, field: 'confidence', value: candidate.confidence })}
                          style={{
                            color: getConfidenceColor(candidate.confidence),
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {(candidate.confidence * 100).toFixed(0)}%
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px' }}>
                      {editingCell?.id === candidate.id && editingCell?.field === 'status' ? (
                        <Select
                          size="small"
                          value={editingCell.value}
                          onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                          onBlur={() => handleCellEdit(candidate.id, 'status', editingCell.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === 'Tab') {
                              e.preventDefault();
                              handleCellEdit(candidate.id, 'status', editingCell.value);
                            }
                          }}
                          style={{ minWidth: '100px' }}
                          autoFocus
                        >
                          <MenuItem value="candidate">Candidate</MenuItem>
                          <MenuItem value="verified">Verified</MenuItem>
                        </Select>
                      ) : (
                        <span
                          onClick={() => setEditingCell({ id: candidate.id, field: 'status', value: candidate.status })}
                          style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 600,
                            backgroundColor: candidate.status === 'verified' ? '#14532d' : '#713f12',
                            color: candidate.status === 'verified' ? '#86efac' : '#fde68a',
                            cursor: 'pointer',
                            textTransform: 'capitalize',
                          }}
                        >
                          {candidate.status}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px', color: theme.palette.text.secondary, fontSize: '12px' }}>
                      {new Date(candidate.updated_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Section>
  );
};

export default ProfilePage;
